import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Snackbar,
  StepIcon,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import api from '../services/api';

// School colors matching the homepage and dashboard
const schoolColors = {
  primary: '#1a237e',
  secondary: '#0d47a1',
  accent: '#ff6f00',
  gold: '#ffd700',
  dark: '#0a0a2a',
  light: '#e3f2fd',
  white: '#ffffff',
  textDark: '#1a1a2e',
  textLight: '#ffffff',
};

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  background: schoolColors.white,
  borderRadius: 24,
  boxShadow: `0 10px 30px ${schoolColors.primary}20`,
  overflow: 'hidden',
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  '& .MuiStepLabel-label.Mui-active': {
    color: schoolColors.accent,
    fontWeight: 600,
  },
  '& .MuiStepLabel-label.Mui-completed': {
    color: schoolColors.primary,
  },
  '& .MuiStepIcon-root.Mui-active': {
    color: schoolColors.accent,
  },
  '& .MuiStepIcon-root.Mui-completed': {
    color: schoolColors.primary,
  },
}));

const StyledButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 8,
  padding: '10px 24px',
  textTransform: 'none',
  fontWeight: 600,
  ...(variant === 'contained' && {
    background: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`,
    '&:hover': {
      background: `linear-gradient(135deg, ${schoolColors.secondary}, ${schoolColors.primary})`,
    },
  }),
}));

const steps = [
  'Personal Information',
  'Education Background',
  'Guardian Information',
  'Medical Information',
  'Review & Submit',
];

const ApplicationForm = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    date_of_birth: null,
    birth_place_district: '',
    birth_place_region: '',
    gender: '',
    citizenship: 'Tanzanian',
    religion: '',
    
    completion_year: '',
    exam_number: '',
    prems_number: '',
    primary_school: '',
    form2_school: '',
    form4_school: '',
    form5_school: '',
    applying_for_level: '',
    
    ward: '',
    district: '',
    region: '',
    
    father: {
      full_name: '',
      address: '',
      citizenship: 'Tanzanian',
      email: '',
      phone: '',
      residence_ward: '',
      residence_district: '',
      residence_region: '',
    },
    mother: {
      full_name: '',
      address: '',
      citizenship: 'Tanzanian',
      email: '',
      phone: '',
      residence_ward: '',
      residence_district: '',
      residence_region: '',
    },
    male_relative: {
      full_name: '',
      address: '',
      citizenship: 'Tanzanian',
      phone: '',
      email: '',
      residence_ward: '',
      residence_district: '',
      residence_region: '',
    },
    female_relative: {
      full_name: '',
      address: '',
      citizenship: 'Tanzanian',
      phone: '',
      email: '',
      residence_ward: '',
      residence_district: '',
      residence_region: '',
    },
    
    medical_info: {
      sight_problem: false,
      hearing_problem: false,
      speech_problem: false,
      blood_problem: false,
      dental_problem: false,
      food_allergy: false,
      food_allergy_details: '',
      blood_group: '',
      chronic_disease: '',
    },
  });

  const validateStep = (step) => {
    const errors = {};
    
    if (step === 0) {
      if (!formData.first_name) errors.first_name = 'First name is required';
      if (!formData.last_name) errors.last_name = 'Last name is required';
      if (!formData.date_of_birth) errors.date_of_birth = 'Date of birth is required';
      if (!formData.gender) errors.gender = 'Gender is required';
      if (!formData.birth_place_district) errors.birth_place_district = 'Birth district is required';
      if (!formData.birth_place_region) errors.birth_place_region = 'Birth region is required';
      if (!formData.citizenship) errors.citizenship = 'Citizenship is required';
      if (!formData.religion) errors.religion = 'Religion is required';
    }
    
    if (step === 1) {
      if (!formData.applying_for_level) errors.applying_for_level = 'Applying for level is required';
      if (!formData.completion_year) errors.completion_year = 'Completion year is required';
      if (!formData.exam_number) errors.exam_number = 'Exam number is required';
      if (!formData.primary_school) errors.primary_school = 'Primary school is required';
      if (!formData.ward) errors.ward = 'Ward is required';
      if (!formData.district) errors.district = 'District is required';
      if (!formData.region) errors.region = 'Region is required';
    }
    
    if (step === 2) {
      if (!formData.father.full_name) errors['father.full_name'] = 'Father/Guardian name is required';
      if (!formData.father.phone) errors['father.phone'] = 'Father/Guardian phone is required';
      if (!formData.mother.full_name) errors['mother.full_name'] = 'Mother/Guardian name is required';
      if (!formData.mother.phone) errors['mother.phone'] = 'Mother/Guardian phone is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      setErrorMessage('Please fill in all required fields');
      setOpenSnackbar(true);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setValidationErrors({});
  };

  const handleChange = (section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
      if (validationErrors[`${section}.${field}`]) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[`${section}.${field}`];
          return newErrors;
        });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
      if (validationErrors[field]) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const formatDateForAPI = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      setErrorMessage('Please fill in all required fields');
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const formattedData = {
        ...formData,
        date_of_birth: formatDateForAPI(formData.date_of_birth),
        completion_year: parseInt(formData.completion_year),
        gender: formData.gender, // Now sends 'ME' or 'KE' directly from dropdown
        father: {
          ...formData.father,
          email: formData.father.email || null,
        },
        mother: {
          ...formData.mother,
          email: formData.mother.email || null,
        },
        male_relative: {
          ...formData.male_relative,
          email: formData.male_relative.email || null,
        },
        female_relative: {
          ...formData.female_relative,
          email: formData.female_relative.email || null,
        },
      };

      console.log('Submitting data:', formattedData);

      const response = await api.post('/applications/', formattedData);
      
      console.log('Response:', response.data);
      
      setRegistrationNumber(response.data.registration_number);
      setSubmitted(true);
    } catch (error) {
      console.error('Application submission failed:', error);
      
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map(err => 
            `${err.loc.join('.')}: ${err.msg}`
          ).join('\n');
          setErrorMessage(errorMessages);
        } else {
          setErrorMessage(error.response.data.detail);
        }
      } else if (error.response?.data) {
        setErrorMessage(JSON.stringify(error.response.data));
      } else {
        setErrorMessage('Failed to submit application. Please try again.');
      }
      
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleChange(null, 'first_name', e.target.value)}
                required
                error={!!validationErrors.first_name}
                helperText={validationErrors.first_name}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Middle Name"
                value={formData.middle_name}
                onChange={(e) => handleChange(null, 'middle_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => handleChange(null, 'last_name', e.target.value)}
                required
                error={!!validationErrors.last_name}
                helperText={validationErrors.last_name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date of Birth"
                  value={formData.date_of_birth}
                  onChange={(date) => handleChange(null, 'date_of_birth', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!validationErrors.date_of_birth,
                      helperText: validationErrors.date_of_birth,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!validationErrors.gender}>
                <InputLabel sx={{ color: validationErrors.gender ? '#f44336' : schoolColors.primary }}>
                  Gender
                </InputLabel>
                <Select
                  value={formData.gender}
                  onChange={(e) => handleChange(null, 'gender', e.target.value)}
                  label="Gender"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: validationErrors.gender ? '#f44336' : `${schoolColors.primary}40`,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: schoolColors.accent,
                    },
                  }}
                >
                  <MenuItem value="ME">Male</MenuItem>
                  <MenuItem value="KE">Female</MenuItem>
                </Select>
                {validationErrors.gender && (
                  <Typography variant="caption" color="error">
                    {validationErrors.gender}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Birth Place - District"
                value={formData.birth_place_district}
                onChange={(e) => handleChange(null, 'birth_place_district', e.target.value)}
                required
                error={!!validationErrors.birth_place_district}
                helperText={validationErrors.birth_place_district}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Birth Place - Region"
                value={formData.birth_place_region}
                onChange={(e) => handleChange(null, 'birth_place_region', e.target.value)}
                required
                error={!!validationErrors.birth_place_region}
                helperText={validationErrors.birth_place_region}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Citizenship"
                value={formData.citizenship}
                onChange={(e) => handleChange(null, 'citizenship', e.target.value)}
                required
                error={!!validationErrors.citizenship}
                helperText={validationErrors.citizenship}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Religion"
                value={formData.religion}
                onChange={(e) => handleChange(null, 'religion', e.target.value)}
                required
                error={!!validationErrors.religion}
                helperText={validationErrors.religion}
              />
            </Grid>
          </Grid>
        );
      
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!validationErrors.applying_for_level}>
                <InputLabel sx={{ color: validationErrors.applying_for_level ? '#f44336' : schoolColors.primary }}>
                  Applying For
                </InputLabel>
                <Select
                  value={formData.applying_for_level}
                  onChange={(e) => handleChange(null, 'applying_for_level', e.target.value)}
                  label="Applying For"
                >
                  <MenuItem value="form1">Form 1</MenuItem>
                  <MenuItem value="form2">Form 2</MenuItem>
                  <MenuItem value="form3">Form 3</MenuItem>
                  <MenuItem value="form5">Form 5</MenuItem>
                </Select>
                {validationErrors.applying_for_level && (
                  <Typography variant="caption" color="error">
                    {validationErrors.applying_for_level}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Completion Year"
                type="number"
                value={formData.completion_year}
                onChange={(e) => handleChange(null, 'completion_year', e.target.value)}
                required
                error={!!validationErrors.completion_year}
                helperText={validationErrors.completion_year}
                inputProps={{ min: 2000, max: 2030 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Exam Number"
                value={formData.exam_number}
                onChange={(e) => handleChange(null, 'exam_number', e.target.value)}
                required
                error={!!validationErrors.exam_number}
                helperText={validationErrors.exam_number}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prems Number"
                value={formData.prems_number}
                onChange={(e) => handleChange(null, 'prems_number', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Primary School"
                value={formData.primary_school}
                onChange={(e) => handleChange(null, 'primary_school', e.target.value)}
                required
                error={!!validationErrors.primary_school}
                helperText={validationErrors.primary_school}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Form 2 School (if applicable)"
                value={formData.form2_school}
                onChange={(e) => handleChange(null, 'form2_school', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Form 4 School (if applicable)"
                value={formData.form4_school}
                onChange={(e) => handleChange(null, 'form4_school', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Form 5 School (if applicable)"
                value={formData.form5_school}
                onChange={(e) => handleChange(null, 'form5_school', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: schoolColors.primary, fontWeight: 600 }}>
                Current Address
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Ward"
                value={formData.ward}
                onChange={(e) => handleChange(null, 'ward', e.target.value)}
                required
                error={!!validationErrors.ward}
                helperText={validationErrors.ward}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="District"
                value={formData.district}
                onChange={(e) => handleChange(null, 'district', e.target.value)}
                required
                error={!!validationErrors.district}
                helperText={validationErrors.district}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Region"
                value={formData.region}
                onChange={(e) => handleChange(null, 'region', e.target.value)}
                required
                error={!!validationErrors.region}
                helperText={validationErrors.region}
              />
            </Grid>
          </Grid>
        );
      
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: schoolColors.primary, fontWeight: 600 }}>
              Father/Guardian Information
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.father.full_name}
                  onChange={(e) => handleChange('father', 'full_name', e.target.value)}
                  required
                  error={!!validationErrors['father.full_name']}
                  helperText={validationErrors['father.full_name']}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address (P.O.Box)"
                  value={formData.father.address}
                  onChange={(e) => handleChange('father', 'address', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Citizenship"
                  value={formData.father.citizenship}
                  onChange={(e) => handleChange('father', 'citizenship', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.father.phone}
                  onChange={(e) => handleChange('father', 'phone', e.target.value)}
                  required
                  error={!!validationErrors['father.phone']}
                  helperText={validationErrors['father.phone']}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.father.email}
                  onChange={(e) => handleChange('father', 'email', e.target.value)}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom sx={{ color: schoolColors.primary, fontWeight: 600 }}>
              Mother/Guardian Information
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.mother.full_name}
                  onChange={(e) => handleChange('mother', 'full_name', e.target.value)}
                  required
                  error={!!validationErrors['mother.full_name']}
                  helperText={validationErrors['mother.full_name']}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address (P.O.Box)"
                  value={formData.mother.address}
                  onChange={(e) => handleChange('mother', 'address', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Citizenship"
                  value={formData.mother.citizenship}
                  onChange={(e) => handleChange('mother', 'citizenship', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.mother.phone}
                  onChange={(e) => handleChange('mother', 'phone', e.target.value)}
                  required
                  error={!!validationErrors['mother.phone']}
                  helperText={validationErrors['mother.phone']}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.mother.email}
                  onChange={(e) => handleChange('mother', 'email', e.target.value)}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom sx={{ color: schoolColors.primary, fontWeight: 600 }}>
              Male Relative (Emergency Contact)
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.male_relative.full_name}
                  onChange={(e) => handleChange('male_relative', 'full_name', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.male_relative.phone}
                  onChange={(e) => handleChange('male_relative', 'phone', e.target.value)}
                  required
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom sx={{ color: schoolColors.primary, fontWeight: 600 }}>
              Female Relative (Emergency Contact)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.female_relative.full_name}
                  onChange={(e) => handleChange('female_relative', 'full_name', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.female_relative.phone}
                  onChange={(e) => handleChange('female_relative', 'phone', e.target.value)}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        );
      
      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: schoolColors.primary, fontWeight: 600 }}>
                Does the student have problems with:
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.medical_info.sight_problem}
                    onChange={(e) => handleChange('medical_info', 'sight_problem', e.target.checked)}
                    sx={{
                      color: schoolColors.primary,
                      '&.Mui-checked': { color: schoolColors.accent },
                    }}
                  />
                }
                label="Sight"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.medical_info.hearing_problem}
                    onChange={(e) => handleChange('medical_info', 'hearing_problem', e.target.checked)}
                    sx={{
                      color: schoolColors.primary,
                      '&.Mui-checked': { color: schoolColors.accent },
                    }}
                  />
                }
                label="Hearing"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.medical_info.speech_problem}
                    onChange={(e) => handleChange('medical_info', 'speech_problem', e.target.checked)}
                    sx={{
                      color: schoolColors.primary,
                      '&.Mui-checked': { color: schoolColors.accent },
                    }}
                  />
                }
                label="Speech"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.medical_info.blood_problem}
                    onChange={(e) => handleChange('medical_info', 'blood_problem', e.target.checked)}
                    sx={{
                      color: schoolColors.primary,
                      '&.Mui-checked': { color: schoolColors.accent },
                    }}
                  />
                }
                label="Blood"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.medical_info.dental_problem}
                    onChange={(e) => handleChange('medical_info', 'dental_problem', e.target.checked)}
                    sx={{
                      color: schoolColors.primary,
                      '&.Mui-checked': { color: schoolColors.accent },
                    }}
                  />
                }
                label="Dental"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.medical_info.food_allergy}
                    onChange={(e) => handleChange('medical_info', 'food_allergy', e.target.checked)}
                    sx={{
                      color: schoolColors.primary,
                      '&.Mui-checked': { color: schoolColors.accent },
                    }}
                  />
                }
                label="Food Allergy"
              />
            </Grid>
            {formData.medical_info.food_allergy && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Food Allergy Details"
                  value={formData.medical_info.food_allergy_details}
                  onChange={(e) => handleChange('medical_info', 'food_allergy_details', e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: schoolColors.primary }}>Blood Group</InputLabel>
                <Select
                  value={formData.medical_info.blood_group}
                  onChange={(e) => handleChange('medical_info', 'blood_group', e.target.value)}
                  label="Blood Group"
                >
                  <MenuItem value="A+">A+</MenuItem>
                  <MenuItem value="A-">A-</MenuItem>
                  <MenuItem value="B+">B+</MenuItem>
                  <MenuItem value="B-">B-</MenuItem>
                  <MenuItem value="O+">O+</MenuItem>
                  <MenuItem value="O-">O-</MenuItem>
                  <MenuItem value="AB+">AB+</MenuItem>
                  <MenuItem value="AB-">AB-</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Chronic Disease (if any)"
                value={formData.medical_info.chronic_disease}
                onChange={(e) => handleChange('medical_info', 'chronic_disease', e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        );
      
      case 4:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3, bgcolor: `${schoolColors.primary}10`, color: schoolColors.primary }}>
              Please review your information before submitting. Incomplete or false information may lead to disqualification.
            </Alert>
            <Typography variant="h6" sx={{ color: schoolColors.primary, fontWeight: 600 }}>Personal Information</Typography>
            <Typography sx={{ mb: 2 }}>
              <strong>Name:</strong> {formData.first_name} {formData.middle_name} {formData.last_name}
              <br />
              <strong>Date of Birth:</strong> {formData.date_of_birth?.toLocaleDateString()}
              <br />
              <strong>Gender:</strong> {formData.gender === 'ME' ? 'Male' : 'Female'}
              <br />
              <strong>Applying for:</strong> {formData.applying_for_level?.replace('form', 'Form ')}
              <br />
              <strong>Birth Place:</strong> {formData.birth_place_district}, {formData.birth_place_region}
              <br />
              <strong>Citizenship:</strong> {formData.citizenship}
              <br />
              <strong>Religion:</strong> {formData.religion}
            </Typography>
            
            <Typography variant="h6" sx={{ mt: 2, color: schoolColors.primary, fontWeight: 600 }}>Education</Typography>
            <Typography sx={{ mb: 2 }}>
              <strong>Completion Year:</strong> {formData.completion_year}
              <br />
              <strong>Exam Number:</strong> {formData.exam_number}
              <br />
              <strong>Primary School:</strong> {formData.primary_school}
              <br />
              <strong>Current Address:</strong> {formData.ward} Ward, {formData.district} District, {formData.region} Region
            </Typography>
            
            <Typography variant="h6" sx={{ mt: 2, color: schoolColors.primary, fontWeight: 600 }}>Declaration</Typography>
            <Typography variant="body2">
              I declare that the information provided is true and correct. I understand that if any information is found to be false, my application will be disqualified.
            </Typography>
          </Box>
        );
      
      default:
        return 'Unknown step';
    }
  };

  if (submitted) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <StyledPaper sx={{ p: { xs: 2.5, md: 4 }, textAlign: 'center' }}>
          
          {/* Header for print/screen */}
          <Box sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`, color: 'white' }}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>KAOZYA SECONDARY SCHOOL</Typography>
            <Typography variant="subtitle1" sx={{ color: schoolColors.gold, fontWeight: 700 }}>OFFICIAL ADMISSION APPLICATION SUMMARY</Typography>
            <Typography variant="caption">Academic Year 2026 • Quality Education For A Better Future</Typography>
          </Box>

          <Alert severity="success" sx={{ mb: 3, fontSize: '1rem', fontWeight: 700, borderRadius: 2 }}>
            Application Submitted Successfully!
          </Alert>

          <Paper elevation={3} sx={{ p: 2.5, bgcolor: `${schoolColors.gold}20`, my: 2, borderRadius: 3, border: `2px dashed ${schoolColors.accent}` }}>
            <Typography variant="body2" sx={{ color: schoolColors.textDark, fontWeight: 700 }}>YOUR REGISTRATION NUMBER:</Typography>
            <Typography variant="h3" sx={{ color: schoolColors.accent, fontWeight: 900, fontFamily: 'monospace', my: 1 }}>
              {registrationNumber}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Please save this official registration number to track your admission status and check examination results.
            </Typography>
          </Paper>

          {/* Full Application Details Breakdown */}
          <Paper variant="outlined" sx={{ p: 3, my: 3, textAlign: 'left', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ color: schoolColors.primary, fontWeight: 800, mb: 2, borderBottom: `2px solid ${schoolColors.primary}20`, pb: 1 }}>
              Applicant Personal &amp; Academic Summary
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Full Name:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{formData.first_name} {formData.middle_name} {formData.last_name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Applying For Level:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{formData.applying_for_level?.replace('form', 'Form ')}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Gender &amp; Date of Birth:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{formData.gender === 'ME' ? 'Male' : 'Female'} • {formData.date_of_birth ? new Date(formData.date_of_birth).toLocaleDateString() : '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Citizenship &amp; Religion:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{formData.citizenship} • {formData.religion}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Primary School &amp; Exam No:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{formData.primary_school} (Exam No: {formData.exam_number || '—'})</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Residential Address:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{formData.ward} Ward, {formData.district}, {formData.region}</Typography>
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ color: schoolColors.primary, fontWeight: 800, mt: 3, mb: 2, borderBottom: `2px solid ${schoolColors.primary}20`, pb: 1 }}>
              Parents &amp; Guardians Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Father / Guardian Name &amp; Phone:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{formData.father?.full_name || '—'} ({formData.father?.phone || '—'})</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Mother / Guardian Name &amp; Phone:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{formData.mother?.full_name || '—'} ({formData.mother?.phone || '—'})</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Actions: Print PDF & Track */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 3 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => window.print()}
              sx={{ bgcolor: schoolColors.primary, color: 'white', fontWeight: 800, borderRadius: 2, px: 4, py: 1.5 }}
            >
              Print / Save As PDF (Fomu ya Maombi)
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/track')}
              sx={{ color: schoolColors.primary, borderColor: schoolColors.primary, fontWeight: 800, borderRadius: 2, px: 4, py: 1.5 }}
            >
              Track Application Status
            </Button>
          </Box>
        </StyledPaper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <StyledPaper>
        <Box sx={{ p: 3, bgcolor: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ color: schoolColors.white, fontWeight: 700 }}>
            Kaozya Secondary School
          </Typography>
          <Typography variant="subtitle1" sx={{ color: schoolColors.gold }}>
            Admission Application Form
          </Typography>
          <Typography variant="body2" sx={{ color: schoolColors.white }}>
            Academic Year 2026
          </Typography>
        </Box>
        
        <Box sx={{ p: 3 }}>
          <StyledStepper activeStep={activeStep} sx={{ my: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </StyledStepper>
          
          <Box sx={{ mt: 2 }}>
            {getStepContent(activeStep)}
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={handleBack}
                sx={{ mr: 1, color: schoolColors.primary }}
                disabled={activeStep === 0}
              >
                Back
              </Button>
              {activeStep === steps.length - 1 ? (
                <StyledButton
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Submit Application'}
                </StyledButton>
              ) : (
                <StyledButton
                  variant="contained"
                  onClick={handleNext}
                >
                  Next
                </StyledButton>
              )}
            </Box>
          </Box>
        </Box>
      </StyledPaper>

      {/* Error Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ApplicationForm;
